import { useEffect, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "../lib/api";

// Admin-only banner. Shows ONLY items that are completely out of stock so the
// owner knows what to restock. We intentionally never expose low-stock counts
// to the storefront — that scarcity nudge is reserved for the cart add toast.
export default function OutOfStockBanner() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    // threshold=0 → only items with 0 stock are returned
    api.get("/admin/low-stock", { params: { threshold: 0 } })
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]));
  }, []);

  if (!items.length) return null;

  const byProduct = items.reduce((acc, r) => {
    if (!acc[r.product_id]) acc[r.product_id] = { title: r.title, image_url: r.image_url, slug: r.slug, lines: [] };
    acc[r.product_id].lines.push(r);
    return acc;
  }, {});
  const groups = Object.values(byProduct);

  return (
    <div data-testid="out-of-stock-alert" className="mt-6 rounded-2xl border border-[#c93875]/60 bg-[#c93875]/[0.08]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full p-4 flex items-center gap-3 text-left"
        data-testid="out-of-stock-toggle"
      >
        <AlertTriangle className="text-[#c93875] flex-shrink-0" size={18}/>
        <div className="flex-1">
          <div className="font-bold uppercase tracking-[0.2em] text-[#c93875] text-xs">
            Out of Stock · {items.length} {items.length === 1 ? "item" : "items"} need restocking
          </div>
          <div className="text-[#4a4239] text-sm mt-0.5">
            {groups.length === 1
              ? `${groups[0].title} is sold out — restock to keep selling.`
              : `${groups.length} products are sold out — restock to keep selling.`}
          </div>
        </div>
        {open ? <ChevronUp size={16} className="text-[#c93875]"/> : <ChevronDown size={16} className="text-[#c93875]"/>}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2" data-testid="out-of-stock-list">
          {groups.map((g) => (
            <div key={g.slug} className="flex items-start gap-3 p-3 rounded-xl bg-white border border-[#e5dccd]">
              {g.image_url && <img src={g.image_url} alt="" className="w-12 h-12 object-cover rounded-lg"/>}
              <div className="flex-1">
                <div className="font-bold text-[#1c1815] text-sm">{g.title}</div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {g.lines.map((l, idx) => (
                    <span
                      key={idx}
                      data-testid={`out-of-stock-${l.product_id}-${l.size || "main"}`}
                      className="text-[10px] uppercase tracking-[0.18em] font-bold px-2.5 py-1 rounded-full bg-[#c93875] text-white border border-[#c93875]"
                    >
                      {l.size ? `Size ${l.size}` : "Stock"} · Sold Out
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
